import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { tokens } from "../../theme";
import { API_BASE_URL } from "../../config/api";
import BookingDetailDialog from "../dailyTap/BookingDetailDialog";
import {
  GOLD,
  MONTHS_FULL,
  WEEKDAYS,
  addDays,
  formatDateLabel,
  formatTime,
  getCalendarCells,
  isSameDay,
  parseTapDateTime,
  productTypeCode,
  productTypeColor,
  startOfDay,
  terminalNumber,
  toDateKey,
} from "../dailyTap/tapUtils";
import { fetchFlightsByIata, normalizeFlightIata } from "./flightApi";

const CARD = {
  departure: "linear-gradient(180deg, #F8E38A 0%, #F5C518 100%)",
  returning: "linear-gradient(90deg, #29B6F6 0%, #81D4FA 100%)",
  landed: "linear-gradient(180deg, #C8E6C9 0%, #66BB6A 100%)",
  noflight: "linear-gradient(180deg, #EEEEEE 0%, #BDBDBD 100%)",
};

const weekMonday = (value) => addDays(value, -((value.getDay() + 6) % 7));

const FlightCard = ({ item, onClick }) => {
  const code = productTypeCode(item.booking.productType);
  return (
    <Box
      onClick={onClick}
      sx={{
        width: 310,
        minHeight: 92,
        borderRadius: "10px",
        background: CARD[item.tone],
        color: "#111",
        cursor: "pointer",
        display: "flex",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0,0,0,0.22)" },
      }}
    >
      <Box
        width={64}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontWeight={800}
        fontSize="1.05rem"
        sx={{ borderRight: "1px solid rgba(0,0,0,0.12)" }}
      >
        {item.timeLabel}
      </Box>
      <Box flex={1} px={1.2} py={0.8} minWidth={0}>
        <Typography fontWeight={800} fontSize="0.95rem" sx={{ textTransform: "uppercase" }} noWrap>
          {item.booking.name || "—"}
        </Typography>
        <Typography fontSize="0.78rem" noWrap>
          {item.booking.productName || item.booking.productType || "—"}
        </Typography>
        <Typography fontSize="0.72rem" fontWeight={700} noWrap>
          {item.booking.reg || "TBC"}
          {item.flightIata ? ` · ${item.flightIata}` : ""}
          {item.landed ? " · LANDED" : ""}
        </Typography>
      </Box>
      <Box
        width={58}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        py={0.6}
      >
        <Typography fontWeight={800} fontSize="1.6rem" lineHeight={1}>
          {terminalNumber(
            item.kind === "departure" ? item.booking.arrivalTerminal : item.booking.returnTerminal
          )}
        </Typography>
        <Box
          px={0.6}
          py={0.15}
          borderRadius="3px"
          bgcolor={productTypeColor(code)}
          color="#fff"
          fontSize="0.65rem"
          fontWeight={800}
        >
          {code === "SELF" ? "SP" : code}
        </Box>
      </Box>
    </Box>
  );
};

const Flights = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const today = startOfDay(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMonth, setViewMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [flightMap, setFlightMap] = useState({});
  const [flightMeta, setFlightMeta] = useState({ fetched: 0, cached: 0 });
  const [flightLoading, setFlightLoading] = useState(false);

  const dateKey = toDateKey(selectedDate);

  const fetchTapBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/signup/getTapBookings`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch TAP bookings");
      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setBookings(data.items);
      } else {
        setError("Failed to load TAP bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTapBookings();
  }, []);

  const filteredByType = useMemo(() => {
    const active = bookings.filter(
      (booking) => String(booking.bookingStatus || "").toUpperCase() !== "CANCELLED"
    );
    if (typeFilter === "ALL") return active;
    return active.filter((booking) => productTypeCode(booking.productType) === typeFilter);
  }, [bookings, typeFilter]);

  const dayDepartures = useMemo(
    () =>
      filteredByType.filter((booking) => {
        const date = parseTapDateTime(booking.arrivalDateTime);
        return date && isSameDay(date, selectedDate);
      }),
    [filteredByType, selectedDate]
  );

  const dayReturns = useMemo(
    () =>
      filteredByType.filter((booking) => {
        const date = parseTapDateTime(booking.returnDateTime);
        return date && isSameDay(date, selectedDate);
      }),
    [filteredByType, selectedDate]
  );

  const neededIatas = useMemo(() => {
    const codes = dayReturns
      .map((booking) => normalizeFlightIata(booking.rtnFlight))
      .filter(Boolean);
    return [...new Set(codes)];
  }, [dayReturns]);

  const loadFlightTimes = async (force = false) => {
    if (!neededIatas.length) {
      setFlightMap({});
      setFlightMeta({ fetched: 0, cached: 0 });
      return;
    }
    setFlightLoading(true);
    try {
      const payload = await fetchFlightsByIata(neededIatas, { force });
      setFlightMap(payload.results);
      setFlightMeta({ fetched: payload.fetched, cached: payload.cached });
    } finally {
      setFlightLoading(false);
    }
  };

  useEffect(() => {
    loadFlightTimes(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededIatas.join("|")]);

  const timelineItems = useMemo(() => {
    const items = [];

    dayDepartures.forEach((booking) => {
      const place = parseTapDateTime(booking.arrivalDateTime);
      if (!place) return;
      items.push({
        id: `dep-${booking._id}`,
        kind: "departure",
        tone: "departure",
        booking,
        place,
        timeLabel: formatTime(booking.arrivalDateTime),
        flightIata: null,
        landed: false,
      });
    });

    dayReturns.forEach((booking) => {
      const iata = normalizeFlightIata(booking.rtnFlight);
      const fallback = parseTapDateTime(booking.returnDateTime);
      if (!fallback) return;

      if (!iata) {
        items.push({
          id: `ret-${booking._id}`,
          kind: "return",
          tone: "noflight",
          booking,
          place: fallback,
          timeLabel: formatTime(booking.returnDateTime),
          flightIata: null,
          landed: false,
        });
        return;
      }

      const live = flightMap[iata];
      const london = live?.ok ? live.london : null;
      const useLive = london && london.dateKey === dateKey;
      const place = useLive
        ? new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            london.hour,
            london.minute
          )
        : fallback;
      const landed = Boolean(live?.ok && live.landed);

      items.push({
        id: `ret-${booking._id}`,
        kind: "return",
        tone: landed ? "landed" : "returning",
        booking,
        place,
        timeLabel: useLive ? london.label : formatTime(booking.returnDateTime),
        flightIata: iata,
        landed,
      });
    });

    return items.sort((a, b) => a.place.getTime() - b.place.getTime());
  }, [dayDepartures, dayReturns, flightMap, dateKey, selectedDate]);

  const hourRows = useMemo(() => {
    const groups = [];
    timelineItems.forEach((item) => {
      const hour = item.place.getHours();
      const last = groups[groups.length - 1];
      if (last && last.hour === hour) last.items.push(item);
      else groups.push({ hour, items: [item] });
    });
    return groups;
  }, [timelineItems]);

  const weekDays = useMemo(() => {
    const monday = weekMonday(selectedDate);
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
  }, [selectedDate]);

  const selectCalendarDate = (cell) => {
    setSelectedDate(cell);
    setCalendarAnchor(null);
  };

  return (
    <Box m="20px">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        px={2.5}
        py={1.5}
        mb={2}
        borderRadius="6px"
        sx={{ backgroundColor: GOLD }}
      >
        <Typography variant="h3" fontWeight={800} color="#1a1a1a">
          Daily {dateKey}
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5}>
          <ButtonGroup variant="contained" size="small">
            {[
              { id: "ALL", label: "All" },
              { id: "MG", label: "MG" },
              { id: "PR", label: "PR" },
              { id: "SELF", label: "Self Park" },
            ].map((option) => (
              <Button
                key={option.id}
                onClick={() => setTypeFilter(option.id)}
                sx={{
                  backgroundColor: typeFilter === option.id ? "#1a1a1a" : "#fff",
                  color: typeFilter === option.id ? GOLD : "#1a1a1a",
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: typeFilter === option.id ? "#333" : "#f3f3f3",
                  },
                }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <Tooltip title="Reload TAP bookings (does not spend flight credits)">
            <IconButton
              onClick={fetchTapBookings}
              sx={{ backgroundColor: "#fff", "&:hover": { backgroundColor: "#f3f3f3" } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh live arrival times (uses AviationStack credits for uncached flights)">
            <span>
              <IconButton
                onClick={() => loadFlightTimes(true)}
                disabled={flightLoading || !neededIatas.length}
                sx={{ backgroundColor: "#fff", "&:hover": { backgroundColor: "#f3f3f3" } }}
              >
                <FlightTakeoffIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" gap={1} mb={2} flexWrap="wrap">
        {weekDays.map((day) => {
          const selected = isSameDay(day, selectedDate);
          return (
            <Button
              key={toDateKey(day)}
              onClick={() => setSelectedDate(day)}
              sx={{
                minWidth: 92,
                backgroundColor: selected ? GOLD : colors.primary[400],
                color: selected ? "#1a1a1a" : colors.grey[100],
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: selected ? GOLD : colors.primary[500] },
              }}
            >
              {day.toLocaleDateString("en-GB", { weekday: "long" })}
            </Button>
          );
        })}
      </Box>

      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <Button
          variant="contained"
          onClick={(event) => setCalendarAnchor(event.currentTarget)}
          sx={{
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: "1.05rem",
            px: 3,
            py: 1.2,
            textTransform: "none",
            "&:hover": { backgroundColor: colors.primary[400], opacity: 0.92 },
          }}
        >
          {formatDateLabel(selectedDate, today)}
        </Button>
        <Box display="flex" alignItems="center" gap={2} mt={1}>
          <IconButton onClick={() => setSelectedDate(addDays(selectedDate, -1))} color="inherit">
            <ChevronLeftIcon />
          </IconButton>
          <Button
            onClick={() => setSelectedDate(today)}
            sx={{ color: colors.grey[100], fontWeight: 700, textTransform: "none" }}
          >
            Today
          </Button>
          <IconButton onClick={() => setSelectedDate(addDays(selectedDate, 1))} color="inherit">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box p={2} width={300} bgcolor={colors.primary[400]}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography fontWeight={800} color={colors.grey[100]}>
              {MONTHS_FULL[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </Typography>
            <Box>
              <IconButton
                size="small"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                }
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                }
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" mb={0.5}>
            {WEEKDAYS.map((day, index) => (
              <Typography
                key={`${day}-${index}`}
                textAlign="center"
                fontSize="0.75rem"
                color={colors.grey[300]}
                fontWeight={700}
              >
                {day}
              </Typography>
            ))}
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
            {getCalendarCells(viewMonth).map((cell, index) => {
              if (!cell) return <Box key={`empty-${index}`} height={36} />;
              const selected = isSameDay(cell, selectedDate);
              const isTodayCell = isSameDay(cell, today);
              return (
                <Button
                  key={toDateKey(cell)}
                  onClick={() => selectCalendarDate(cell)}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    p: 0,
                    borderRadius: "50%",
                    backgroundColor: selected ? GOLD : "transparent",
                    color: selected ? "#1a1a1a" : colors.grey[100],
                    fontWeight: selected || isTodayCell ? 800 : 500,
                    border: isTodayCell && !selected ? `1px solid ${GOLD}` : "none",
                    "&:hover": {
                      backgroundColor: selected ? GOLD : colors.primary[500],
                    },
                  }}
                >
                  {cell.getDate()}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Popover>

      <Box display="flex" gap={2} mb={1.5} flexWrap="wrap" px={0.5}>
        {[
          { tone: "departure", label: "Departure" },
          { tone: "returning", label: "Return (live ETA)" },
          { tone: "landed", label: "Landed" },
          { tone: "noflight", label: "No rtnFlight" },
        ].map((entry) => (
          <Box key={entry.tone} display="flex" alignItems="center" gap={0.8}>
            <Box width={18} height={12} borderRadius="3px" sx={{ background: CARD[entry.tone] }} />
            <Typography fontSize="0.8rem" color={colors.grey[200]}>
              {entry.label}
            </Typography>
          </Box>
        ))}
        <Typography fontSize="0.8rem" color={colors.grey[300]}>
          {flightLoading
            ? "Looking up live arrivals…"
            : `Flight lookups this load: ${flightMeta.fetched} live / ${flightMeta.cached} cached`}
        </Typography>
      </Box>

      <Box
        bgcolor="#f4f4f4"
        borderRadius="8px"
        minHeight={420}
        p={2}
        sx={{ color: "#111" }}
      >
        {loading && <Box p={3}>Loading TAP bookings…</Box>}
        {error && (
          <Box p={3} color="#b71c1c">
            Error: {error}
          </Box>
        )}
        {!loading && !error && timelineItems.length === 0 && (
          <Box p={3} color="#555">
            No departures or returns for {formatDateLabel(selectedDate, today)}.
          </Box>
        )}
        {hourRows.map((row, index) => (
          <Box
            key={row.hour}
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            py={1.5}
            sx={{
              borderBottom: index === hourRows.length - 1 ? "none" : "1px dashed #bdbdbd",
            }}
          >
            {row.items.map((item) => (
              <FlightCard
                key={item.id}
                item={item}
                onClick={() => setSelectedBooking(item.booking)}
              />
            ))}
          </Box>
        ))}
      </Box>

      <BookingDetailDialog
        open={Boolean(selectedBooking)}
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
      />
    </Box>
  );
};

export default Flights;
